import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createRaptorPlatform,
  deactivateRaptorPlatform,
  getRaptorPlatformById,
  getRaptorPlatformsForClient,
  raptorPlatformKeys,
  updateRaptorPlatform,
} from './routes';
import { getAllFundManagersForClient } from '../../client';

// Query Hooks

/**
 * Hook to fetch all RaptorPlatforms for a client
 */
export const useRaptorPlatforms = (
  client_id: string,
  include_inactive: boolean = false,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    gcTime?: number;
  },
) => {
  return useQuery({
    queryKey: raptorPlatformKeys.list(client_id, include_inactive),
    queryFn: () => getRaptorPlatformsForClient(client_id, include_inactive),
    enabled: options?.enabled ?? !!client_id,
    staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutes
    gcTime: options?.gcTime ?? 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to fetch a specific RaptorPlatform by ID
 */
export const useRaptorPlatform = (
  client_id: string,
  raptor_platform_id: string,
  include_inactive: boolean = false,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    gcTime?: number;
  },
) => {
  return useQuery({
    queryKey: raptorPlatformKeys.detail(
      client_id,
      raptor_platform_id,
      include_inactive,
    ),
    queryFn: () =>
      getRaptorPlatformById(client_id, raptor_platform_id, include_inactive),
    enabled: options?.enabled ?? !!(client_id && raptor_platform_id),
    staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutes
    gcTime: options?.gcTime ?? 10 * 60 * 1000, // 10 minutes
  });
};

// Mutation Hooks

/**
 * Hook to create a new RaptorPlatform
 */
export const useCreateRaptorPlatform = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createRaptorPlatform,
    onSuccess: (data, variables) => {
      // Invalidate and refetch platform lists for the client
      queryClient.invalidateQueries({
        queryKey: raptorPlatformKeys.lists(),
      });

      // Optionally add the new platform to the cache
      queryClient.setQueryData(
        raptorPlatformKeys.detail(variables.client_id, data.raptor_platform_id),
        data,
      );
    },
    onError: (error) => {
      console.error('Failed to create RaptorPlatform:', error);
    },
  });
};

/**
 * Hook to update an existing RaptorPlatform
 */
export const useUpdateRaptorPlatform = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateRaptorPlatform,
    onSuccess: (data, variables) => {
      // Update the specific platform in cache
      queryClient.setQueryData(
        raptorPlatformKeys.detail(
          variables.client_id,
          variables.raptor_platform_id,
        ),
        data,
      );

      // Invalidate platform lists to ensure consistency
      queryClient.invalidateQueries({
        queryKey: raptorPlatformKeys.lists(),
      });
    },
    onError: (error) => {
      console.error('Failed to update RaptorPlatform:', error);
    },
  });
};

/**
 * Hook to deactivate a RaptorPlatform
 */
export const useDeactivateRaptorPlatform = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      client_id,
      raptor_platform_id,
    }: {
      client_id: string;
      raptor_platform_id: string;
    }) => deactivateRaptorPlatform(client_id, raptor_platform_id),
    onSuccess: (_, variables) => {
      // Remove the platform from cache
      queryClient.removeQueries({
        queryKey: raptorPlatformKeys.detail(
          variables.client_id,
          variables.raptor_platform_id,
        ),
      });

      // Invalidate platform lists to reflect the change
      queryClient.invalidateQueries({
        queryKey: raptorPlatformKeys.lists(),
      });
    },
    onError: (error) => {
      console.error('Failed to deactivate RaptorPlatform:', error);
    },
  });
};

// Utility Hooks

/**
 * Hook to prefetch RaptorPlatforms for a client
 */
export const usePrefetchRaptorPlatforms = () => {
  const queryClient = useQueryClient();

  return (client_id: string, include_inactive: boolean = false) => {
    queryClient.prefetchQuery({
      queryKey: raptorPlatformKeys.list(client_id, include_inactive),
      queryFn: () => getRaptorPlatformsForClient(client_id, include_inactive),
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };
};

/**
 * Hook to prefetch a specific RaptorPlatform
 */
export const usePrefetchRaptorPlatform = () => {
  const queryClient = useQueryClient();

  return (
    client_id: string,
    raptor_platform_id: string,
    include_inactive: boolean = false,
  ) => {
    queryClient.prefetchQuery({
      queryKey: raptorPlatformKeys.detail(
        client_id,
        raptor_platform_id,
        include_inactive,
      ),
      queryFn: () =>
        getRaptorPlatformById(client_id, raptor_platform_id, include_inactive),
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };
};

export const useAllFundManagersForClient = (clientId: string) => {
  return useQuery({
    queryKey: ['all-fund-managers-for-client', clientId],
    queryFn: () => getAllFundManagersForClient(clientId),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
